/**
 * Signups from the waitlist pages (/ehr, /rate-check), sent with the answers
 * to the practice questions. The Apps Script in waitlist.gs, whose header has
 * the setup steps, adds each one to a Google Sheet and emails the person and
 * the team.
 *
 * The body is sent as text/plain so the browser skips the CORS preflight,
 * which Apps Script cannot answer. Production also needs
 * https://script.google.com and https://script.googleusercontent.com in the
 * gateway CSP's connect-src.
 */

/** The Apps Script web app URL (ends in /exec). */
export const WAITLIST_ENDPOINT =
  "https://script.google.com/macros/s/AKfycby5Fzfg5OP-wiS84yY-Ze8Cmw8xpJ6VWQuCcSkI1E3B9Bpd7nckB8kVYSZEMQAPuchWSw/exec";

/**
 * The Sheet's page column and the leads' lead_source. /ehr keeps "free-ehr",
 * its first address, so its place count, repeat check and confirmation email
 * in waitlist.gs carry on from the signups made there.
 */
export type WaitlistPage = "free-ehr" | "rate-check";

/** The practice questions; each key is a column of the Sheet. */
export interface WaitlistAnswers {
  role: string;
  providers: string;
  /** Asked on /ehr only. */
  current_ehr?: string;
}

export interface WaitlistSignup extends WaitlistAnswers {
  page: WaitlistPage;
  name: string;
  email: string;
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
const PRODUCTION_HOSTS = ["altair-health.com", "www.altair-health.com"];

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Add one signup to the Sheet, with the ad's UTM tags. Resolves to the
 * signup's place in line, or null while the script keeps it hidden. Throws if
 * the signup was not stored.
 */
export async function joinWaitlist(
  signup: WaitlistSignup,
  endpoint: string = WAITLIST_ENDPOINT,
): Promise<number | null> {
  const params = new URLSearchParams(window.location.search);
  const utms = Object.fromEntries(UTM_KEYS.map((key) => [key, params.get(key) ?? ""]));

  if (!endpoint) {
    // Preview deployments have no Sheet, so the form can be tried end to end.
    if (PRODUCTION_HOSTS.includes(window.location.hostname)) {
      throw new Error("WAITLIST_ENDPOINT is not set");
    }
    return null;
  }

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify({ ...signup, ...utms }),
  });
  const result = (await res.json().catch(() => null)) as { ok?: unknown; place?: unknown } | null;
  if (!res.ok || result?.ok !== true) {
    throw new Error(`Waitlist signup was not stored (HTTP ${res.status})`);
  }
  return Number.isInteger(result.place) && (result.place as number) > 0 ? (result.place as number) : null;
}
